import type { Enabler, EnablerSubmission } from '@/lib/supabase'
import type { EnablerFormData } from '@/types/forms'
import { callBridge } from './apiBridge'
import { callAdminBridge } from './adminBridge'
import { captureError } from '@/lib/sentry'

interface EnvironmentConfig {
  url: string
  anonKey: string
}

export async function submitEnabler(input: EnablerFormData, organizationId?: string): Promise<{ success: boolean; id?: string; error?: string }>{
  try {
    const payload = organizationId ? { ...input, organizationId } : input
    const result = await callBridge('submit_enabler', payload)
    return { success: true, id: result.id }
  } catch (error: any) {
    return { success: false, error: error?.message || 'Failed to submit enabler' }
  }
}

export async function getMyEnablerSubmission(): Promise<EnablerSubmission | null> {
  try {
    const result = await callBridge('get_my_enabler_submission')
    return result
  } catch (error) {
    // Log to Sentry for observability - silent failures should be tracked
    console.error('Error fetching enabler submission:', error)
    captureError(error instanceof Error ? error : new Error(String(error)), {
      tags: { function: 'getMyEnablerSubmission' }
    })
    return null
  }
}

export async function updateEnablerSubmission(id: string, input: EnablerFormData, organizationId?: string): Promise<{ success: boolean; error?: string }>{
  try {
    const payload = organizationId ? { id, ...input, organizationId } : { id, ...input }
    await callBridge('update_enabler_submission', payload)
    return { success: true }
  } catch (error: any) {
    return { success: false, error: error?.message || 'Failed to update enabler profile' }
  }
}

export async function listEnablerSubmissions(envConfig?: EnvironmentConfig): Promise<EnablerSubmission[]> {
  try {
    const result = await callAdminBridge('list_enabler_submissions', {}, {
      ...(envConfig && { envUrl: envConfig.url, envAnonKey: envConfig.anonKey })
    })
    return result
  } catch (error) {
    // Log to Sentry for observability - silent failures should be tracked
    console.error('Error listing enabler submissions:', error)
    captureError(error instanceof Error ? error : new Error(String(error)), {
      tags: { function: 'listEnablerSubmissions' }
    })
    return []
  }
}

export async function listEnablers(envConfig?: EnvironmentConfig): Promise<Enabler[]> {
  try {
    const result = await callAdminBridge('list_enablers', {}, {
      ...(envConfig && { envUrl: envConfig.url, envAnonKey: envConfig.anonKey })
    })
    return result
  } catch (error) {
    // Log to Sentry for observability - silent failures should be tracked
    console.error('Error listing enablers:', error)
    captureError(error instanceof Error ? error : new Error(String(error)), {
      tags: { function: 'listEnablers' }
    })
    return []
  }
}

export async function updateEnablerSubmissionStatus(id: string, status: 'approved' | 'rejected', adminNotes?: string, envConfig?: EnvironmentConfig): Promise<{ success: boolean; error?: string }>{
  try {
    await callAdminBridge('update_enabler_submission_status', { id, status, adminNotes }, {
      ...(envConfig && { envUrl: envConfig.url, envAnonKey: envConfig.anonKey })
    })
    return { success: true }
  } catch (error: any) {
    return { success: false, error: error?.message || 'Failed to update status' }
  }
}

export async function approveEnabler(id: string, envConfig?: EnvironmentConfig): Promise<{ success: boolean; error?: string }>{
  try {
    await callAdminBridge('approve_enabler', { id }, {
      ...(envConfig && { envUrl: envConfig.url, envAnonKey: envConfig.anonKey })
    })
    return { success: true }
  } catch (error: any) {
    return { success: false, error: error?.message || 'Failed to approve enabler' }
  }
}
